import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { Store } from '@ngxs/store';
import { map, take, takeUntil } from 'rxjs/operators';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { Subject } from 'rxjs';
import { SelectModel } from '@shared/components/select/models/select';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SharedState } from '@shared/state/shared/shared.state';
import { GetDictionaryValues } from '@shared/state/shared/shared.action';
import { CatalogType, CatalogView } from '@shared/models/shared-catalog.state';
import { SelectComponent } from '@shared/components/select/components/select/select.component';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CatalogFilter } from '@shared/components/filter-catalog-items/models';
import {
  CatalogDynamicColumns,
  CatalogItemsQueryParams,
  SearchCatalogItem
} from '../../../../../data/modules/catalog-items-data/models/catalog-item-component';
import { CatalogItemsState } from '../../../../../data/modules/catalog-items-data/store/catalog-items-state';
import { ConnectedPosition } from "@angular/cdk/overlay";

@Component({
  selector: 'app-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class SearchFilterComponent implements OnInit, IDestroy {
  private _separator = ' and ';
  private _allFields: SearchCatalogItem[];
  private _dialogRef: MatDialogRef<TemplateRef<any>>;
  private _hasTopFilter = false;
  public componentDestroy: () => Subject<any>;
  public filterEmpty = true;
  public isMobile = false;
  public isRecommendationData: boolean;
  public form: UntypedFormGroup = new UntypedFormGroup({
    radius: new UntypedFormControl({ value: 2000, disabled: true }),
    isNextMeData: new UntypedFormControl(false)
  });
  public selectCatalog: CatalogView;
  @ViewChildren('appSelect') public appSelects: QueryList<SelectComponent>;
  @ViewChild('filterTemp') public filterTemp: TemplateRef<any>;
  @Output() public readonly changedFilter = new EventEmitter<CatalogFilter>();
  isDateFieldFocused = false;
  public get allFilterFields(): SearchCatalogItem[] {
    return this._allFields;
  }

  @Input()
  public set allFields(fields: CatalogDynamicColumns[] | null) {
    this.filterEmpty = true;
    if (fields) {
      this._allFields = fields
        .filter(x => x.type !== CatalogType.LINK)
        .map(field => {
          const result = new SearchCatalogItem(
            '',
            field.type,
            field.name,
            field.title,
            field?.dictionaryId
              ? this.store.select(SharedState.getDictionaryValue).pipe(
                  map(filterFn => filterFn(field.dictionaryId as number)),
                  map(res =>
                    res.map(dic => ({
                      value: dic.title,
                      title: dic.title
                    }))
                  )
                )
              : undefined,
            field.dictionaryId,
            true,
            field.title.includes('<sup>'),
            field.title.length > 55
          );
          if (field?.dictionaryId) {
            this.loadingSelectOptions(result);
          }

          return result;
        });

      this.setFilterOptions();
    }
  }
  @Input() public hasBorder = false;
  @Input() public overlayPosition: ConnectedPosition[] = [];
  public dictionaryType = CatalogType.Dictionary;
  public flagType = CatalogType.Flag;
  public integerType = CatalogType.Integer;
  public numericType = CatalogType.Numeric;
  public numberType = CatalogType.Number;
  public dateType = CatalogType.Date;

  public flagSelectionOptions: SelectModel[] = [
    {
      title: 'Да',
      value: 'true'
    },
    {
      title: 'Нет',
      value: 'false'
    }
  ];

  public icons = CustomIcons;
  public isOpenSearchColumn = false;
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 700px)', '(max-width: 768px)', '(max-width: 1100px)']).pipe(
    map(res => {
      this.isMobile = res.breakpoints['(max-width: 700px)'];
      this._hasTopFilter = res.breakpoints['(max-width: 768px)'];
      return this.isMobile;
    })
  );
  constructor(
    private store: Store,
    private router: Router,
    private location: Location,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef
  ) {}

  private getFilter(): string {
    let criteria = '';

    this.allFilterFields.forEach(currentFilter => {
      if (currentFilter.searchValue) {
        let searchString = currentFilter.searchValue.toString().trim();
        if (searchString.length > 0) {
          if (criteria) {
            criteria += this._separator;
          }
          switch (currentFilter.type) {
            case CatalogType.String:
            case CatalogType.Text:
              searchString = ` ilike '%${searchString}%'`;
              break;
            case CatalogType.Dictionary:
              searchString = ` = '${searchString}'`;
              break;
            case CatalogType.Timestamp:
              // eslint-disable-next-line
              searchString = ` = \'${searchString}\'`;
              break;
            default:
              searchString = ` = ${searchString}`;
              break;
          }

          criteria += `${currentFilter.name}${searchString}`;
        }
      }
    });
    return criteria;
  }

  private setFilterOptions(): void {
    const params = this.router.parseUrl(this.location.path()).queryParams as CatalogItemsQueryParams;
    if (params?.filter) {
      let splitParams = params.filter.split(' and ');
      if (splitParams.length === 0) {
        splitParams = [params.filter];
      }
      splitParams.forEach(paramStr => {
        const paramsArray = paramStr.split(' ');
        const paramsName = paramsArray.shift();
        paramsArray.shift(); // извлекли оператор
        const paramsValue = paramsArray.join(' ').replace(/'/g, '');
        if (paramsName && paramsValue) {
          const foundField = this._allFields.find(field => field.name === paramsName);
          if (foundField) {
            foundField.searchValue = paramsValue.replace(/[%]/g, '');
          }
        }
      });
      this.filterEmpty = false;
    }
  }

  private loadingSelectOptions(element: SearchCatalogItem): void {
    if (element.dictionaryId) {
      this.store
        .dispatch(new GetDictionaryValues(element.dictionaryId))
        .pipe(take(1), takeUntil(this.componentDestroy()))
        .subscribe(() => {
          element.loading = false;
        });
    }
  }

  public ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.componentDestroy())).subscribe(res => {
      if (res?.isNextMeData) {
        this.form.controls.radius.enable({
          emitEvent: false
        });
      } else {
        this.form.controls.radius.disable({
          emitEvent: false
        });
      }

      if (res?.radius > 5000) {
        this.form.controls.radius?.setValue(5000, {
          emitEvent: false
        });
      } else if (res?.radius < 100) {
        this.form.controls.radius?.setValue(100, {
          emitEvent: false
        });
      }
    });
    this.selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);

    if (!this.selectCatalog) {
      return;
    }
    this.store
      .select(CatalogItemsState.getCatalogItemIsNextToMeData)
      .pipe(
        map(filterFn => filterFn(this.selectCatalog.id)),
        takeUntil(this.componentDestroy())
      )
      .subscribe(nextToMyData => {
        if (nextToMyData) {
          this.form.controls.radius?.setValue(nextToMyData.radius ?? 2000, {
            emitEvent: false
          });
          if (nextToMyData.radius) {
            this.form.controls.radius?.enable({
              emitEvent: false
            });
          } else {
            this.form.controls.radius?.disable({
              emitEvent: false
            });
          }

          this.form.controls.isNextMeData?.setValue(nextToMyData.isNextMeData, {
            emitEvent: false
          });
        }
      });

    this.store
      .select(CatalogItemsState.getCatalogItemIsRecommendationData)
      .pipe(
        map(filterFn => filterFn(this.selectCatalog.id)),
        takeUntil(this.componentDestroy())
      )
      .subscribe(isRecommendationData => {
        this.isRecommendationData = isRecommendationData ?? false;
      });
  }

  public onElementScroll(): void {
    this.appSelects.forEach(appSelect => {
      if (appSelect.isOpen) {
        appSelect.closePanel();
      }
    });
  }

  public confirmFilter(): void {
    const criteria = this.getFilter();
    this.isOpenSearchColumn = false;
    this.setDefaultZIndexModal();
    this.filterEmpty = !criteria;
    const filterProps: CatalogFilter = this._hasTopFilter
      ? {
          criteria: criteria,
          nextMeData: this.form.value,
          isRecommendationData: this.isRecommendationData,
          isMobile: this.isMobile
        }
      : { criteria: criteria, isMobile: this.isMobile };

    if (!this.isMobile) {
      if (!this.selectCatalog) {
        this.selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
      }
      const isRecommendationData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsRecommendationData).call(this, this.selectCatalog.id);
      const nextMeData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, this.selectCatalog.id);
      if (nextMeData) {
        filterProps.nextMeData = nextMeData as any;
      }
      if (isRecommendationData) {
        filterProps.isRecommendationData = isRecommendationData;
      }
    }

    this.changedFilter.emit(filterProps);

    this.closeModal();
  }

  public cancelFilter(isEmitEvent = true): void {
    this.allFilterFields.forEach(x => {
      x.searchValue = '';
    });
    this.filterEmpty = true;
    this.isOpenSearchColumn = false;
    this.setDefaultZIndexModal();
    let filterProps: CatalogFilter;

    if (this._hasTopFilter) {
      this.isRecommendationData = false;

      this.form.controls.radius?.setValue(2000, {
        emitEvent: false
      });

      this.form.controls.isNextMeData?.setValue(false, {
        emitEvent: false
      });
      filterProps = {
        isMobile: this.isMobile,
        criteria: '',
        nextMeData: this.form.value,
        isRecommendationData: this.isRecommendationData
      };
    } else if (!this.isMobile) {
      if (!this.selectCatalog) {
        this.selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
      }
      const isRecommendationData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsRecommendationData).call(this, this.selectCatalog.id);
      const nextMeData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, this.selectCatalog.id);
      filterProps = { criteria: '', isMobile: this.isMobile };
      if (nextMeData) {
        filterProps.nextMeData = nextMeData as any;
      }
      if (isRecommendationData) {
        filterProps.isRecommendationData = isRecommendationData;
      }
    } else {
      filterProps = { criteria: '', isMobile: this.isMobile };
    }
    if (isEmitEvent) {
      this.changedFilter.emit(filterProps);
      this.closeModal();
    } else {
      this.cdr.detectChanges();
    }
  }

  public openModal(): void {
    const config = this.isMobile
      ? {
          maxWidth: '100%',
          width: '100%',
          height: '100%',
          position: {
            top: '0'
          },
          panelClass: 'mobile-bottom-show-up-search-filter-dialog',
          backdropClass: 'backdrop',
          hasBackdrop: true
        }
      : {
          maxWidth: '100%',
          width: '100%',
          height: 'calc(100% - 70px)',
          position: {
            bottom: '0'
          }
        };

    this._dialogRef = this.dialog.open(this.filterTemp, config);
  }

  public closeModal(): void {
    if (this._dialogRef) {
      this._dialogRef.close();
    }
  }

  public setDefaultZIndexModal(): void {
    const node = window.document.querySelector<any>('.cdk-overlay-transparent-backdrop');
    if (node) {
      node.parentNode.style.zIndex = '';
    }
  }

  public openFilterMenu(): void {
    this.isOpenSearchColumn = !this.isOpenSearchColumn;
    this.setFilterOptions();
    if (window) {
      setTimeout(() => {
        if (window.document.querySelector<any>('.cdk-overlay-transparent-backdrop')) {
          window.document.querySelector<any>('.cdk-overlay-transparent-backdrop').parentNode.style.zIndex = '59';
        }
      });
    }
  }

  onDateFieldFocus(focused = false) {
    this.isDateFieldFocused = focused;
  }
}
